import { Code as MantineCode, CodeProps, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedCodeProps extends CodeProps {
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  block?: boolean;
  language?: string;
  /** Polymorphic component to render as (e.g., 'a', 'pre', Link) */
  component?: any;
  /** Route path when using component prop with React Router Link */
  to?: string;
  /** Standard anchor href for direct links (when component="a") */
  href?: string;
  /** Target for anchor links (when component="a") */
  target?: string;
  /** Rel attribute for anchor links (when component="a") */
  rel?: string;
}

const Code = forwardRef<HTMLElement, ThemedCodeProps>(
  ({ gradient = false, gradientVariant = 'primary', block = false, language, children, style, ...rest }, ref) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';

    const baseProps = {
      ref,
      component: block ? 'pre' : undefined,
      style: { display: block ? 'block' : undefined, ...style },
      'data-language': language,
      ...rest,
    };

    if (!gradient) {
      return <MantineCode {...baseProps}>{children}</MantineCode>;
    }

    const gradientConfig = theme.other.gradients[gradientVariant];

    return (
      <MantineCode
        {...baseProps}
        style={{
          backgroundImage: `linear-gradient(135deg, ${gradientConfig.from}15, ${gradientConfig.to}15)`,
          borderLeft: `3px solid ${gradientConfig.from}`,
          color: isDark ? theme.colors.gray[3] : theme.colors.dark[7],
          ...baseProps.style,
        }}
      >
        {children}
      </MantineCode>
    );
  }
);

Code.displayName = 'Code';

export default Code;
